const DEBUG_QUERY_PARAM = "ga_debug";
const DEBUG_STORAGE_KEY = "ga_debug";

const GA_ENDPOINT_PATTERNS = [
  "google-analytics.com/g/collect",
  "google-analytics.com/mp/collect",
  "googletagmanager.com",
];

declare global {
  interface Window {
    __gaDebugInstalled__?: boolean;
  }
}

function isGaDebugEnabled(): boolean {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const debugParam = searchParams.get(DEBUG_QUERY_PARAM);

    if (debugParam === "1") {
      localStorage.setItem(DEBUG_STORAGE_KEY, "1");
      return true;
    }

    if (debugParam === "0") {
      localStorage.removeItem(DEBUG_STORAGE_KEY);
      return false;
    }

    return localStorage.getItem(DEBUG_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function isGaRequest(url: string): boolean {
  return GA_ENDPOINT_PATTERNS.some((pattern) => url.includes(pattern));
}

function safeParseBody(body?: unknown): string | undefined {
  if (!body) return undefined;
  if (typeof body === "string") return body;
  if (body instanceof URLSearchParams) return body.toString();
  return `[${(body as { constructor?: { name?: string } }).constructor?.name || "Body"}]`;
}

function installTrackingDebug(): void {
  if (!isGaDebugEnabled() || window.__gaDebugInstalled__) return;

  window.__gaDebugInstalled__ = true;

  const originalSendBeacon = navigator.sendBeacon?.bind(navigator);
  if (originalSendBeacon) {
    navigator.sendBeacon = (url: string | URL, data?: unknown) => {
      const urlString = url.toString();
      const result = originalSendBeacon(
        url,
        data as Parameters<typeof originalSendBeacon>[1],
      );

      if (isGaRequest(urlString)) {
        console.log("[GA Debug] sendBeacon", {
          url: urlString,
          queued: result,
          body: safeParseBody(data),
        });
      }

      return result;
    };
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const [input, init] = args;
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;

    const response = await originalFetch(...args);

    if (isGaRequest(url)) {
      console.log("[GA Debug] fetch", {
        url,
        method:
          init?.method ||
          (typeof input !== "string" && !(input instanceof URL)
            ? input.method
            : "GET"),
        status: response.status,
        ok: response.ok,
        body: safeParseBody(init?.body),
      });
    }

    return response;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null,
  ) {
    (this as XMLHttpRequest & { __gaDebugUrl__?: string }).__gaDebugUrl__ =
      url.toString();
    return originalOpen.call(
      this,
      method,
      url,
      async ?? true,
      username,
      password,
    );
  };

  XMLHttpRequest.prototype.send = function (body?: unknown) {
    const request = this as XMLHttpRequest & { __gaDebugUrl__?: string };
    const url = request.__gaDebugUrl__;

    if (url && isGaRequest(url)) {
      this.addEventListener("loadend", () => {
        console.log("[GA Debug] xhr", {
          url,
          status: this.status,
          body: safeParseBody(body),
        });
      });
    }

    return originalSend.call(this, body as Parameters<typeof originalSend>[0]);
  };

  console.log(
    `[GA Debug] enabled. Add ?${DEBUG_QUERY_PARAM}=1 to the URL or set localStorage.${DEBUG_STORAGE_KEY}=1`,
  );
}

export { installTrackingDebug, isGaDebugEnabled };
